import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import TicketQR from '../components/TicketQR';
import { fmtDate, fullName } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';

// Pantalla de una invitación (/i/:code). Nadie pagó nada: la entrada existe recién
// cuando la persona completa sus datos y acepta.
//
// El texto habla del evento de ORIGEN ("como viniste a la KANT") porque ese es el
// motivo de la invitación, y es lo que hace que no se lea como spam de un boliche
// desconocido — que es justamente el caso de uso: gente que todavía no conoce LOBBO.
export default function Invitacion() {
  const { code } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ buyer_name: '', buyer_lastname: '', buyer_birthdate: '', buyer_dni: '', buyer_phone: '', buyer_city: '', marketing_consent: false });
  const [ticket, setTicket] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/public/invitacion/${code}`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) { setError(d.error || 'Invitación no encontrada'); return; }
        setInv(d);
        // El nombre viene de la lista con la que se armó la campaña: se precarga para
        // que aceptar sea lo más corto posible.
        setForm(f => ({ ...f, buyer_name: d.name || '', buyer_lastname: d.lastname || '' }));
      })
      .catch(() => setError('Error de conexión'));
  }, [code]);

  async function aceptar(e) {
    e.preventDefault();
    if (!form.buyer_name.trim() || !form.buyer_lastname.trim()) { alert('Nombre y apellido obligatorios'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/public/invitacion/${code}/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'No se pudo aceptar'); return; }
      setTicket(d);
    } catch { alert('Error de conexión'); } finally { setSaving(false); }
  }

  if (error) return <Shell><div style={{ color: 'var(--red-300)', textAlign: 'center' }}>{error}</div></Shell>;
  if (!inv) return <Shell><div style={{ color: 'var(--ink-600)', textAlign: 'center' }}>Cargando…</div></Shell>;

  // Ya la había aceptado antes: se le muestra su QR en vez de un error. Volver a entrar
  // al link es lo primero que hace cualquiera que quiere ver su entrada de nuevo.
  const qr = ticket?.qr_code || inv.qr_code;
  if (qr) return (
    <Shell logo={inv.logo_url} name={inv.org_name}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div className="lb-eyebrow">Tu entrada está lista</div>
        <div className="lb-display" style={{ fontSize: 24, marginTop: 6 }}>{inv.event_name}</div>
        <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 4 }}>
          {fullName(ticket?.buyer_name || form.buyer_name, ticket?.buyer_lastname || form.buyer_lastname)}{inv.lot_name ? ` · ${inv.lot_name}` : ''}
        </div>
      </div>
      <TicketQR value={qr} transportValue={ticket?.transport_qr || null}
        logo={inv.logo_url} eventName={inv.event_name} date={inv.date} lotName={inv.lot_name}
        attendeeName={fullName(ticket?.buyer_name || form.buyer_name, ticket?.buyer_lastname || form.buyer_lastname)}
        filename={`${inv.event_name}-${qr}.png`} />
      <p style={{ color: 'var(--ink-600)', fontSize: 12, textAlign: 'center', marginTop: 20 }}>
        Descargá o sacale captura. Presentalo en la puerta.
      </p>
    </Shell>
  );

  if (inv.vencida) return (
    <Shell logo={inv.logo_url} name={inv.org_name}>
      <div style={{ textAlign: 'center' }}>
        <div className="lb-display" style={{ fontSize: 26, color: 'var(--red-400)' }}>La invitación venció</div>
        <p style={{ color: 'var(--ink-700)', marginTop: 12, lineHeight: 1.6 }}>
          Esta invitación tenía tiempo limitado y ya pasó. Podés comprar tu entrada para {inv.event_name} como siempre.
        </p>
      </div>
    </Shell>
  );

  if (inv.sin_cupo) return (
    <Shell logo={inv.logo_url} name={inv.org_name}>
      <div style={{ textAlign: 'center' }}>
        <div className="lb-display" style={{ fontSize: 26, color: 'var(--red-400)' }}>Se agotaron los lugares</div>
        <p style={{ color: 'var(--ink-700)', marginTop: 12, lineHeight: 1.6 }}>
          Las invitaciones para {inv.event_name} eran limitadas y ya se completaron. Podés comprar tu entrada como siempre.
        </p>
      </div>
    </Shell>
  );

  const gratis = !Number(inv.lot_price);
  return (
    <Shell logo={inv.logo_url} name={inv.org_name}>
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div className="lb-eyebrow" style={{ color: 'var(--red-400)' }}>Tenés una invitación</div>
        <div className="lb-display" style={{ fontSize: 28, marginTop: 8 }}>{inv.event_name}</div>
        <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 6, textTransform: 'capitalize' }}>
          {fmtDate(inv.date)}{inv.start_time ? ` · ${inv.start_time}` : ''}
        </div>
        {inv.venue && <div style={{ color: 'var(--ink-600)', fontSize: 12, marginTop: 2 }}>{inv.venue}{inv.city ? ` · ${inv.city}` : ''}</div>}
      </div>

      {inv.source_event_name && (
        <div style={{ background: 'rgba(139,15,26,0.12)', border: '1px solid rgba(139,15,26,0.45)', borderRadius: 6, padding: '14px 16px', marginBottom: 18, textAlign: 'center' }}>
          <div style={{ color: '#fff', fontSize: 14, lineHeight: 1.5 }}>
            Como viniste a <b>{inv.source_event_name}</b>, {inv.org_name} te invita a esta noche.
          </div>
        </div>
      )}

      <div style={{ background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 4, padding: '14px 18px', marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: '#fff', textTransform: 'uppercase' }}>{inv.lot_name || 'Invitación'}</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: gratis ? '#2ecc71' : '#fff' }}>
          {gratis ? 'Sin cargo' : `$${Number(inv.lot_price).toLocaleString('es-AR')}`}
        </div>
      </div>

      <form onSubmit={aceptar} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <F label="Nombre"><input className="lb-input" value={form.buyer_name} onChange={e => set('buyer_name', e.target.value)} required /></F>
          <F label="Apellido"><input className="lb-input" value={form.buyer_lastname} onChange={e => set('buyer_lastname', e.target.value)} required /></F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <F label="Fecha de nacimiento"><input className="lb-input" type="date" value={form.buyer_birthdate} onChange={e => set('buyer_birthdate', e.target.value)} /></F>
          <F label="Localidad"><input className="lb-input" value={form.buyer_city} onChange={e => set('buyer_city', e.target.value)} /></F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <F label="DNI"><input className="lb-input" inputMode="numeric" value={form.buyer_dni} onChange={e => set('buyer_dni', e.target.value.replace(/\D/g, ''))} /></F>
          <F label="Teléfono"><input className="lb-input" inputMode="tel" value={form.buyer_phone} onChange={e => set('buyer_phone', e.target.value)} /></F>
        </div>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.45, cursor: 'pointer', marginTop: 4 }}>
          <input type="checkbox" checked={form.marketing_consent} onChange={e => set('marketing_consent', e.target.checked)} style={{ marginTop: 2 }} />
          <span>Quiero recibir novedades y promociones de <b style={{ color: '#fff' }}>{inv.org_name}</b> por email. <span style={{ color: 'var(--ink-600)' }}>(Podés darte de baja cuando quieras.)</span></span>
        </label>
        <button className="lb-btn lb-btn--primary lb-btn--block" disabled={saving} style={{ marginTop: 6 }}>
          {saving ? 'Generando…' : 'Aceptar invitación y generar mi QR →'}
        </button>
      </form>

      {inv.expires_at && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', textAlign: 'center', marginTop: 12 }}>
          Tenés tiempo hasta el {new Date(inv.expires_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' })} hs.
        </div>
      )}
    </Shell>
  );
}

function F({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}

function Shell({ children, logo, name }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
      {logo
        ? <img src={logo} alt={name || ''} style={{ height: 42, marginBottom: 24, objectFit: 'contain' }} />
        : <div style={{ marginBottom: 24, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: '#fff', textTransform: 'uppercase' }}>{name || ''}</div>}
      <div style={{ width: '100%', maxWidth: 460 }}>{children}</div>
    </div>
  );
}
